// What a workspace can do (GET /api/customers/{c}/capabilities,
// routes/capabilities.py; docs/no-google.md §9): where it keeps its files
// (Google Drive or Corvus storage), the operator's linked account on Corvus
// storage, and what that switches off (sharing, Projects › Access, agents'
// own Google, the inbox watcher, booking pages, email out). Not in the
// iPhone contract.
//
// While the answer loads, and if it cannot be read, every screen behaves as
// on a Google Drive workspace (DRIVE_CAPABILITIES): today's behaviour, so a
// Google workspace never hides anything. A Corvus-storage workspace may show
// a Google-only button for a moment; its route refuses it.

import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

type Generated = components["schemas"]["Capabilities"];
export type LinkedAccountCapability = components["schemas"]["LinkedAccountCapability"];
export type LibraryCaps = components["schemas"]["LibraryCaps"];
// Every field filled in (the generated type marks the server's defaults optional).
export type Capabilities = Required<Omit<Generated, "linked_account">> & {
  linked_account: Required<LinkedAccountCapability>;
};

export const NO_LINKED_ACCOUNT: Required<LinkedAccountCapability> = {
  provider: "",
  email: "",
  display_name: "",
  state: "none",
  mail: false,
  drafts: false,
  calendar: false,
  offered: [],
};

// A Google Drive workspace (the library caps are services/library_index's
// MAX_FILES and MAX_DEPTH, what the server answers for one).
export const DRIVE_CAPABILITIES: Capabilities = {
  file_store: "drive",
  store_read_only: false,
  linked_account: NO_LINKED_ACCOUNT,
  sharing: true,
  drive_access: true,
  agent_google: true,
  inbox_watcher: true,
  booking_pages: true,
  email_out: true,
  library: { max_files: 2000, max_depth: 4 },
};

export const capabilitiesKeys = {
  view: (customerId: string) => ["capabilities", customerId] as const,
};

export function withDefaults(raw: Generated | undefined | null): Capabilities {
  if (!raw) return DRIVE_CAPABILITIES;
  return {
    ...DRIVE_CAPABILITIES,
    ...Object.fromEntries(Object.entries(raw).filter(([, v]) => v !== undefined && v !== null)),
    linked_account: { ...NO_LINKED_ACCOUNT, ...(raw.linked_account ?? {}) },
    library: raw.library ?? DRIVE_CAPABILITIES.library,
  } as Capabilities;
}

// The query itself (loading and error states), for a screen that needs them.
export function useCapabilitiesQuery(customerId: string | null) {
  return useQuery({
    queryKey: capabilitiesKeys.view(customerId ?? ""),
    enabled: Boolean(customerId),
    queryFn: ({ signal }) =>
      apiFetch<Generated>(`/api/customers/${encodeURIComponent(customerId ?? "")}/capabilities`, { signal }),
    staleTime: 60_000,
  });
}

// The capabilities, Google Drive's until the answer is in.
export function useCapabilities(customerId: string | null): Capabilities {
  const { data } = useCapabilitiesQuery(customerId);
  return withDefaults(data);
}

export function isCorvusStorage(caps: Pick<Capabilities, "file_store">): boolean {
  return caps.file_store === "corvus";
}
